/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRiskPolicyApprovalsQueryParamStatus,
  ListRiskPolicyApprovalsRequest,
  ListRiskPolicyApprovalsSecurity,
} from "../models/operations/listriskpolicyapprovals.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskListApprovalsQuery,
  prefetchRiskListApprovals,
  queryKeyRiskListApprovals,
  RiskListApprovalsQueryData,
} from "./riskListApprovals.core.js";
export {
  buildRiskListApprovalsQuery,
  prefetchRiskListApprovals,
  queryKeyRiskListApprovals,
  type RiskListApprovalsQueryData,
};

export type RiskListApprovalsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRiskPolicyApprovals risk
 *
 * @remarks
 * List tool approval requests held by require_approval policies, newest first. Org admins see every request; others see requests they can approve and their own.
 */
export function useRiskListApprovals(
  request?: ListRiskPolicyApprovalsRequest | undefined,
  security?: ListRiskPolicyApprovalsSecurity | undefined,
  options?: QueryHookOptions<
    RiskListApprovalsQueryData,
    RiskListApprovalsQueryError
  >,
): UseQueryResult<RiskListApprovalsQueryData, RiskListApprovalsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskListApprovalsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRiskPolicyApprovals risk
 *
 * @remarks
 * List tool approval requests held by require_approval policies, newest first. Org admins see every request; others see requests they can approve and their own.
 */
export function useRiskListApprovalsSuspense(
  request?: ListRiskPolicyApprovalsRequest | undefined,
  security?: ListRiskPolicyApprovalsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskListApprovalsQueryData,
    RiskListApprovalsQueryError
  >,
): UseSuspenseQueryResult<
  RiskListApprovalsQueryData,
  RiskListApprovalsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskListApprovalsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskListApprovalsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      status?: ListRiskPolicyApprovalsQueryParamStatus | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskListApprovalsQueryData,
): RiskListApprovalsQueryData | undefined {
  const key = queryKeyRiskListApprovals(...queryKeyBase);

  return client.setQueryData<RiskListApprovalsQueryData>(key, data);
}

export function invalidateRiskListApprovals(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      status?: ListRiskPolicyApprovalsQueryParamStatus | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "approvals", "list", ...queryKeyBase],
  });
}

export function invalidateAllRiskListApprovals(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "approvals", "list"],
  });
}
